import {useMutation, useQueryClient} from "@tanstack/react-query";
import {useEffect, useRef, useState} from "react";
import {useLocation, useNavigate} from "react-router-dom";

import {
  CUSTOMER_ADDRESSES_KEY,
  formatCustomerAddress,
  pickCustomerPhone,
  updateCustomerAddress,
  type CustomerAddress,
} from "@/api/endpoints/address";
import {getProfile, type CustomerAppUser} from "@/api/endpoints/auth";
import {ApiError, API_ERROR_CODES} from "@/api/client";
import {useBackGuard} from "@/app/back";
import {notify} from "@/app/notify";
import {ROUTES} from "@/app/routes";
import {PinView, type PinViewHandle} from "@/components/ui/PinView";
import {ScreenHeader} from "@/components/ui/ScreenHeader";

export interface ChangeAddressConfirmState {
  current: CustomerAddress;
  next: CustomerAddress;
  askPin?: boolean;
}

const WRONG_PIN_MESSAGE = "รหัส PIN ไม่ถูกต้อง";
function isRecoverable(error: unknown): boolean {
  if (!(error instanceof ApiError)) {
    return false;
  }
  const pinBusinessCode =
    typeof error.businessCode === "string" &&
    error.businessCode.startsWith("PIN_");
  const pinText =
    typeof error.message === "string" &&
    error.message.toLowerCase().includes("pin") &&
    error.message.includes("ไม่ถูกต้อง");
  const pinHttp400 =
    error.code === API_ERROR_CODES.http &&
    error.httpStatus === 400 &&
    pinText;
  return pinBusinessCode || pinHttp400;
}

function newRequestId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `addr-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export default function ChangeAddressConfirmPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const state = location.state as ChangeAddressConfirmState | null;
  const [agreed, setAgreed] = useState(state?.askPin === true);
  const [askingPin, setAskingPin] = useState(state?.askPin === true);
  const [profile, setProfile] = useState<CustomerAppUser | undefined>(undefined);
  const pinRef = useRef<PinViewHandle>(null);
  const submittingRef = useRef(false);
  const requestIdRef = useRef(newRequestId());

  async function loadCustomerData() {
    try {
      const user = await getProfile();
      setProfile(user);
    } catch (error) {
      console.error("Error fetching customer data:", error);
    }
  }

  useEffect(() => {
    void loadCustomerData();
  }, []);

  const submit = useMutation({
    mutationFn: (pinCode: string) => {
      if (profile === undefined || state === null) {
        throw new Error("Cannot submit before the customer and address load.");
      }
      return updateCustomerAddress({
        pinCode,
        accountNo: state.next.accountNo,
        oldAddress: state.current,
        newAddress: state.next,
        requestId: requestIdRef.current,
      });
    },
    onSuccess: () => {
      // walk back from the success screen into the address they just replaced.
      void queryClient.invalidateQueries({ queryKey: CUSTOMER_ADDRESSES_KEY });
      void navigate(ROUTES.settings.accountChangeAddressSuccess, {
        replace: true,
      });
    },
    onError: (error) => {
      if (isRecoverable(error)) {
        notify("error", WRONG_PIN_MESSAGE);
        pinRef.current?.clearAll();
        return;
      }
      void navigate(ROUTES.settings.accountChangeAddressError, {
        replace: true,
        state,
      });
    },
    onSettled: () => {
      submittingRef.current = false;
    },
  });

  useBackGuard(() => {
    if (!askingPin) {
      return false;
    }
    setAskingPin(false);
    return true;
  });

  if (state === null) {
    return (
      <>
        <ScreenHeader title="ตรวจสอบที่อยู่"/>
        <main className="safe-bottom flex-1 bg-white px-4 pt-4">
          <p className="text-14 text-field-label leading-5">ไม่พบข้อมูลที่อยู่</p>
        </main>
      </>
    );
  }

  if (askingPin) {
    return (
      <>
        <ScreenHeader title="กรอก PIN Code"/>
        <main className="relative flex flex-1 flex-col items-center bg-white px-4 tracking-[-0.011em] my-16">
          <PinView
            ref={pinRef}
            disabled={profile === undefined || submit.isPending}
            onChange={(pin) => {
              if (pin.length < 6 || submittingRef.current) return;
              submittingRef.current = true;
              submit.mutate(pin);
            }}
          />        </main>
      </>
    );
  }

  return (
    <>
      <ScreenHeader title="ตรวจสอบที่อยู่"/>
      <main className="flex-1 bg-white px-4 pt-4 pb-[calc(120px+env(safe-area-inset-bottom))] tracking-[-0.011em]">
        <p className="flex h-[38px] items-center gap-[10px] rounded-[10px] bg-warning pl-[9px]">
          <InfoIcon/>
          <span className="text-14 leading-[21px] font-normal">
            กรุณาตรวจสอบที่อยู่จัดส่งเอกสาร
          </span>
        </p>

        <h2 className="text-16 mt-5 leading-6 font-normal">
          ที่อยู่จัดส่งเอกสารปัจจุบัน
        </h2>
        <div className="mt-2 rounded-xl border-2 border-field-label px-[17px] pt-[10px] pb-3">
          <span className="flex items-baseline justify-between gap-4">
            <span className="text-16 text-field-label leading-6 font-normal">
              เลขที่สัญญา :
            </span>
            <span className="text-16 text-field-value leading-6 font-medium">
              {state.current.accountNo}
            </span>
          </span>
          <hr className="mt-[14px] border-t border-divider-strong"/>
          <span className="text-16 text-field-label mt-[11px] block leading-6 font-medium">
            ที่อยู่
          </span>
          <span className="text-16 text-field-value block leading-[22.6px] font-medium">
            {formatCustomerAddress(state.current)}
          </span>
          <span className="mt-[5px] flex items-baseline gap-[7px]">
            <span className="text-16 text-field-label leading-6 font-normal">
              เบอร์โทรจัดส่งเอกสาร
            </span>
            <span className="text-16 text-field-value leading-6 font-medium">
              {pickCustomerPhone(state.current)}
            </span>
          </span>
        </div>

        <h2 className="text-16 text-field-label mt-5 leading-6 font-normal">
          ที่อยู่จัดส่งเอกสารใหม่
        </h2>
        <div className="mt-2 rounded-xl border-2 border-success px-[17px] pt-[10px] pb-3">
          <span className="text-16 text-field-label block leading-6 font-medium">
            ที่อยู่
          </span>
          <span className="text-16 text-field-value block leading-[22.6px] font-medium">
            {formatCustomerAddress(state.next)}
          </span>
          <span className="mt-[5px] flex items-baseline gap-[7px]">
            <span className="text-16 text-field-label leading-6 font-normal">
              เบอร์โทรจัดส่งเอกสาร
            </span>
            <span className="text-16 text-field-value leading-6 font-medium">
              {pickCustomerPhone(state.next)}
            </span>
          </span>
        </div>

        <div
          className="bg-warning-banner mt-3 rounded-[8px] px-3 py-3 text-[12px] leading-[18px] font-normal text-[#FF1512]">
          <p className="font-medium text-[#D00200]">
            การเปลี่ยนแปลงนี้จะมีผลกับสัญญาทั้งหมด 1 ฉบับ ดังต่อไปนี้
          </p>
          <p className="mt-1 font-bold text-[#D00200] pl-4">{state.current.accountNo}</p>
          <p className="mt-3 font-medium text-[#FF1512]">
            การเปลี่ยนที่อยู่ไม่ได้เปลี่ยนช่องทางการรับเอกสาร
          </p>
          <p className="mt-1">
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;สัญญาที่คุณเลือกรับเอกสารทางอีเมลหรือช่องทางอิเล็กทรอนิกส์ไว้
            จะยังคงได้รับเอกสารทางช่องทางเดิม ไม่ได้กลับมาเป็นเอกสารกระดาษ
            ที่อยู่ใหม่จะถูกใช้กับเอกสารที่บริษัทต้องจัดส่งเป็นเอกสารกระดาษตามที่กฎหมายหรือสัญญากำหนดเท่านั้น
            หากต้องการเปลี่ยนช่องทางการรับเอกสาร กรุณาติดต่อเจ้าหน้าที่
          </p>

          <p className="mt-3 font-medium text-[#FF1512]">
            ที่อยู่ใหม่จะมีผลเมื่อระบบบันทึกข้อมูลเรียบร้อยแล้ว
          </p>
          <p className="mt-1">
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;เอกสารที่บริษัทจัดพิมพ์หรือส่งเข้ากระบวนการจัดส่งไปก่อนหน้านั้น
            จะยังคงถูกส่งไปยังที่อยู่เดิม
            โดยอาจใช้เวลาประมาณ 30 วันทำการก่อนที่เอกสารทุกฉบับจะถูกส่งไปยังที่อยู่ใหม่
          </p>
        </div>

        <button
          type="button"
          onClick={() => {
            setAgreed((prev) => !prev);
          }}
          className="mt-3 flex w-full items-start gap-3 text-left bg-[#F3F3F3] rounded-[10px] px-3 py-3"
        >
          <span
            aria-hidden="true"
            className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-[6px] border-2 ${
              agreed ? "border-primary bg-primary" : "border-field-label bg-white"
            }`}
          >
            {agreed ? <CheckIcon/> : null}
          </span>
          <span className="text-14 leading-5 font-normal">
            ข้าพเจ้าได้ตรวจสอบที่อยู่ใหม่และรายการสัญญาข้างต้นแล้ว&nbsp;
            <strong className="font-bold">
              ขอรับรองว่าที่อยู่ที่ระบุเป็นที่อยู่ที่ข้าพเจ้าสามารถรับเอกสารได้ด้วยตนเอง
            </strong>{" "}
            และรับทราบว่าการเปลี่ยนแปลงนี้มีผลกับสัญญาทุกฉบับที่ปรากฏข้างต้น
          </span>
        </button>
      </main>

      <div className="fixed inset-x-0 bottom-0 bg-white px-4 pb-[calc(16px+env(safe-area-inset-bottom))] w-full">
        <button
          type="button"
          disabled={!agreed || profile === undefined}
          onClick={() => {
            setAskingPin(true);
          }}
          className="text-24 h-[51px] w-full rounded-[10px] bg-primary text-white disabled:bg-light-gray-5"
        >
          ยืนยันการเปลี่ยนแปลง
        </button>
      </div>
    </>
  );
}

function InfoIcon() {
  return (
    <svg viewBox="0 0 16 16" className="h-4 w-4 shrink-0" aria-hidden="true">
      <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" strokeWidth="1"/>
      <path d="M8 7v4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      <circle cx="8" cy="4.75" r="0.9" fill="currentColor"/>
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4 text-white" aria-hidden="true">
      <path
        d="m5 13 4 4 10-10"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
